/**
 * Period selector store (W12-F2-S4).
 *
 * React Context + localStorage hand-roll matching the W12-F2-S3
 * `currencyStore` pattern (drop-in upgrade to Zustand if/when we add
 * the dep). Holds the primary period + optional compare-to period;
 * persisted across reloads so the user's PoP setup survives a refresh.
 *
 * Period anchors snap to natural boundaries before storing:
 *   month   → first-of-month
 *   quarter → first-of-quarter
 *   year    → first-of-year
 *   ytd     → today (no snapping)
 *   trailing12 → today (rolling window)
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { PeriodKind, PeriodSpec } from '../types';

const STORAGE_KEY = 'senviro.finance.period';

interface PersistedShape {
  primary: PeriodSpec;
  compareTo?: PeriodSpec;
}

const DEFAULT_PRIMARY: PeriodSpec = {
  kind: 'ytd',
  anchor: todayIso(),
};

interface PeriodContextValue {
  primary: PeriodSpec;
  compareTo: PeriodSpec | undefined;
  setPrimary: (next: PeriodSpec) => void;
  setCompareTo: (next: PeriodSpec | undefined) => void;
}

const PeriodContext = createContext<PeriodContextValue | undefined>(undefined);

export function PeriodProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<PersistedShape>(() => {
    if (typeof window === 'undefined') return { primary: DEFAULT_PRIMARY };
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return { primary: DEFAULT_PRIMARY };
      const parsed = JSON.parse(raw) as Partial<PersistedShape>;
      return {
        primary: validateSpec(parsed.primary) ?? DEFAULT_PRIMARY,
        compareTo: validateSpec(parsed.compareTo) ?? undefined,
      };
    } catch {
      return { primary: DEFAULT_PRIMARY };
    }
  });

  const persist = useCallback((next: PersistedShape): void => {
    setState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // localStorage unavailable — fall back to memory only.
    }
  }, []);

  const setPrimary = useCallback(
    (next: PeriodSpec): void => persist({ ...state, primary: snap(next) }),
    [state, persist],
  );

  const setCompareTo = useCallback(
    (next: PeriodSpec | undefined): void =>
      persist({ ...state, compareTo: next ? snap(next) : undefined }),
    [state, persist],
  );

  // Sync across tabs.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    function onStorage(e: StorageEvent): void {
      if (e.key === STORAGE_KEY && typeof e.newValue === 'string') {
        try {
          const parsed = JSON.parse(e.newValue) as Partial<PersistedShape>;
          setState({
            primary: validateSpec(parsed.primary) ?? DEFAULT_PRIMARY,
            compareTo: validateSpec(parsed.compareTo) ?? undefined,
          });
        } catch {
          // ignore — bad write from another tab
        }
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const value = useMemo<PeriodContextValue>(
    () => ({
      primary: state.primary,
      compareTo: state.compareTo,
      setPrimary,
      setCompareTo,
    }),
    [state, setPrimary, setCompareTo],
  );

  return <PeriodContext.Provider value={value}>{children}</PeriodContext.Provider>;
}

export function usePeriod(): PeriodContextValue {
  const ctx = useContext(PeriodContext);
  if (!ctx) {
    return {
      primary: DEFAULT_PRIMARY,
      compareTo: undefined,
      setPrimary: () => undefined,
      setCompareTo: () => undefined,
    };
  }
  return ctx;
}

const VALID_KINDS: ReadonlyArray<PeriodKind> = [
  'month',
  'quarter',
  'year',
  'ytd',
  'trailing12',
];

function validateSpec(input: unknown): PeriodSpec | undefined {
  if (!input || typeof input !== 'object') return undefined;
  const s = input as { kind?: unknown; anchor?: unknown };
  if (typeof s.kind !== 'string' || !VALID_KINDS.includes(s.kind as PeriodKind)) {
    return undefined;
  }
  if (typeof s.anchor !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(s.anchor)) {
    return undefined;
  }
  return { kind: s.kind as PeriodKind, anchor: s.anchor };
}

function snap(spec: PeriodSpec): PeriodSpec {
  const d = new Date(spec.anchor);
  if (Number.isNaN(d.getTime())) return spec;
  switch (spec.kind) {
    case 'month':
      return {
        kind: 'month',
        anchor: isoFromUtc(d.getUTCFullYear(), d.getUTCMonth(), 1),
      };
    case 'quarter': {
      const q = Math.floor(d.getUTCMonth() / 3);
      return { kind: 'quarter', anchor: isoFromUtc(d.getUTCFullYear(), q * 3, 1) };
    }
    case 'year':
      return { kind: 'year', anchor: isoFromUtc(d.getUTCFullYear(), 0, 1) };
    case 'ytd':
    case 'trailing12':
      return spec;
    default:
      return spec;
  }
}

function isoFromUtc(year: number, month: number, day: number): string {
  return new Date(Date.UTC(year, month, day)).toISOString().slice(0, 10);
}

function todayIso(): string {
  const n = new Date();
  return new Date(Date.UTC(n.getUTCFullYear(), n.getUTCMonth(), n.getUTCDate()))
    .toISOString()
    .slice(0, 10);
}
